import React from 'react'

import { Typography, Box } from '@the-platform-group/component-library'
import { Grid } from '@the-platform-group/component-library/Layout'
import { Button } from '@the-platform-group/component-library/Button'
import { Lock as LockIcon } from '@the-platform-group/component-library/Icons'

import { AUTH_ERROR_TYPES } from 'constants/errorCodes'

/**
 * Displays a locked account error page with a logout action
 * @returns {React.ReactElement} The locked account display
 */
function IdentityLockedDisplay() {
  return (
    <Box sx={{ height: '100vh', width: '100%' }}>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        direction="column"
        spacing={2}
        sx={{ height: '100%' }}
      >
        <Grid item>
          <LockIcon color="error" sx={{ fontSize: '60px' }} />
        </Grid>
        <Grid item>
          <Typography variant="h5">Your Account Has Been Locked</Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1">
            Your account has been locked. Please contact your administrator or support team for
            assistance.
          </Typography>
        </Grid>
        <Grid item>
          <Button
            onClick={() => {
              window.location.href = '/logout'
            }}
          >
            Logout
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

/**
 * Displays a not-configured error page when the user's FactSet account lacks the required access codes for Irwin
 * @returns {React.ReactElement} The not-configured display
 */
function NotConfiguredDisplay() {
  return (
    <Box sx={{ height: '100vh', width: '100%' }}>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        direction="column"
        spacing={2}
        sx={{ height: '100%' }}
      >
        <Grid item>
          <LockIcon color="warning" sx={{ fontSize: '60px' }} />
        </Grid>
        <Grid item>
          <Typography variant="h5">Your FactSet Account Is Not Configured for Irwin</Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1">
            Your FactSet account does not have the required access codes to use Irwin. Please
            contact your Irwin representative for assistance.
          </Typography>
        </Grid>
        <Grid item>
          <Button
            onClick={() => {
              window.location.href = '/logout'
            }}
          >
            Logout
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

/**
 * Error boundary for authentication-related errors from the API.
 * Handles identity_locked and not_configured 401 errors that should display a dedicated
 * error page instead of triggering re-authentication.
 */
class AuthenticationErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { errorType: '' }
  }

  static getDerivedStateFromError(error) {
    if (error.response && error.response.data) {
      const { status, data } = error.response
      if (status === 401) {
        if (data.errors?.some(e => e.code === AUTH_ERROR_TYPES.identityLocked)) {
          return { errorType: AUTH_ERROR_TYPES.identityLocked }
        }
        if (data.errors?.some(e => e.code === AUTH_ERROR_TYPES.notConfigured)) {
          return { errorType: AUTH_ERROR_TYPES.notConfigured }
        }
      }
    }

    throw error
  }

  render() {
    switch (this.state.errorType) {
      case AUTH_ERROR_TYPES.identityLocked:
        return <IdentityLockedDisplay />
      case AUTH_ERROR_TYPES.notConfigured:
        return <NotConfiguredDisplay />
      default:
        return this.props.children
    }
  }
}

export default AuthenticationErrorBoundary
